import React, { useMemo } from 'react';
import { Minus, Plus } from 'lucide-react';
import { extractTextFromContent, countWords } from '../lib/textExtract';

/**
 * WordCountBar (T-032) — discreet status bar with the active sheet's word count
 * and the whole-document total. Counts mirror the backend `count_words`.
 *
 * T-117: when `zoom` (number) and `onZoomChange` are both passed in, the bar
 * also renders the document-zoom control (`[-] 100% [+]`) that previously lived
 * in the ContinuousDocument toolbar. EditorPage owns the state and normalizes
 * the value (range 0.5–2, step 0.1, persisted in localStorage); this bar only
 * renders and dispatches, so it never clamps.
 *
 * Perf: `sheets` only changes when the debounced autosave persists
 * (ContinuousDocument onUpdate → updateSheetContent → setSheets), i.e. at
 * autosave cadence, not per keystroke. So a plain useMemo over
 * [sheets, activeSheetId] is enough — no extra debounce needed.
 */
const WordCountBar = ({ sheets, activeSheetId, zoom, onZoomChange }) => {
  const { sheetWords, docWords } = useMemo(() => {
    const list = sheets || [];
    const active = list.find((s) => s.id === activeSheetId);
    const sheet = active ? countWords(extractTextFromContent(active.content)) : 0;
    const doc = list.reduce(
      (sum, s) => sum + countWords(extractTextFromContent(s.content)),
      0,
    );
    return { sheetWords: sheet, docWords: doc };
  }, [sheets, activeSheetId]);

  const fmt = (n) => n.toLocaleString('es-MX');

  // T-117: el control de zoom solo se renderiza cuando EditorPage pasa un valor
  // numerico y un handler. En hojas de calculo, EditorPage pasa `undefined` para
  // ambos, asi que el control (y su separador) no aparecen.
  const showZoom = typeof zoom === 'number' && typeof onZoomChange === 'function';

  return (
    <div className="flex-shrink-0 flex items-center flex-wrap justify-end gap-2 px-3 md:px-6 py-1.5 border-t border-white/20 text-xs text-slate-500 dark:text-slate-400 select-none">
      <span>Hoja: {fmt(sheetWords)} palabras</span>
      <span aria-hidden="true">·</span>
      <span>Documento: {fmt(docWords)} palabras</span>
      {showZoom && (
        <>
          <span aria-hidden="true">·</span>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={() => onZoomChange(zoom - 0.1)}
              title="Alejar"
              className="p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
            >
              <Minus className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onZoomChange(1)}
              title="Restablecer zoom (100%)"
              className="text-xs tabular-nums w-11 py-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => onZoomChange(zoom + 0.1)}
              title="Acercar"
              className="p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WordCountBar;
